import { Factory } from "@/data/dao/Factory";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IArticle, IArticleOptionGroup, ITableViewArticle } from "@/data/model/Article";
import type { IOfferCategory } from "@/data/model/OfferCategory";
import { Format } from "@/data/util/Format";

import { BaseModal } from "@/shared/elements/BaseModal";
import { translate } from "@/shared/localization/language";

import { i18n } from "@/portal/Trans";
import { DataTable, type IDataTableConfig } from "@/portal/elements/DataTable";
import { TableFilter } from "@/portal/elements/TableFilter";
import { PortalFormat } from "@/portal/util/PortalFormat";

export class AddOptionToGroupWindow extends BaseModal {
    public onSelected?: { (articles: Array<IArticle>) };

    private $tableFilter: JQuery;
    private $table: JQuery;
    private $cancelBtn: JQuery;
    private $submitBtn: JQuery;

    private dao: CatalogDao;
    private dataTable?: DataTable<ITableViewArticle>;
    private search?: TableFilter;

    private selected: Array<IArticle>;
    private alreadyAdded = new Array<string>();

    constructor() {
        super(i18n("modal.addOptionToGroup.title"), "modal-xl", true);

        this.$element.addClass("addoption");

        this.dao = Factory.getInstance<CatalogDao>(CatalogDao);

        this.$tableFilter = $('<div class="tablefilter"></div>').appendTo(this.$body);

        this.$table = $(
            '<div class="kt-datatable kt-datatable--default kt-datatable--brand kt-datatable--loaded"></div>'
        ).appendTo(this.$body);

        this.selected = new Array<IArticle>();
        this.initDataTable();

        this.$cancelBtn = $(
            '<button type="button" class="btn btn-secondary">' + i18n("general.cancel") + "</button>"
        ).appendTo(this.$footer!);
        this.$submitBtn = $('<button type="button" class="btn btn-success">' + i18n("general.save") + "</button>")
            .appendTo(this.$footer!)
            .click((e) => {
                e.preventDefault();

                if (this.selected.length === 0) return;

                if (this.onSelected !== undefined) this.onSelected(this.selected);

                this.resetSelected();
                this.close();
            });

        this.checkSubmitBtn();
    }

    private initDataTable() {
        const tableConfig: IDataTableConfig<ITableViewArticle> = {
            columnMapping: [
                {
                    name: "",
                    property: "id",
                    styles: { width: "5%" },
                    formatter: (m, p) => {
                        const isChecked = this.selected.find((c) => c.id === p) !== undefined;
                        const lbl = $('<label class="checkbox checkbox-lg"><span></span></label>');
                        const checkBox = $(
                            '<input type="checkbox" ' + (isChecked ? 'checked="checked"' : "") + " />"
                        ).prependTo(lbl);
                        checkBox.click((e) => {
                            e.stopPropagation();
                            e.preventDefault();

                            window.setTimeout(() => {
                                const a = $(e.currentTarget).parent().parent().parent();
                                this.checkUncheckArticle(a.data("model"), a);
                            }, 10);
                        });

                        return lbl;
                    },
                },
                {
                    name: i18n("general.entities.item"),
                    property: "internalName",
                    styles: { width: "21%" },
                    formatter: (m) => {
                        if (m.internalName !== undefined) return Format.htmlEscape(m.internalName);

                        return Format.htmlEscape(translate(m.name));
                    },
                },
                {
                    name: i18n("modal.addOptionToGroup.categories"),
                    property: "offerCategories",
                    styles: { width: "21%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IOfferCategory>(
                            p,
                            (c) => Format.htmlEscape(translate(c.name))!,
                            undefined,
                            4
                        );
                    },
                },
                {
                    name: i18n("general.entities.optionGroups"),
                    property: "optionGroups",
                    styles: { width: "21%" },
                    formatter: (m, p) => {
                        return PortalFormat.toCommaSeparatedList<IArticleOptionGroup>(
                            p,
                            (c) => Format.htmlEscape(c.internalName)!,
                            undefined,
                            4
                        );
                    },
                },
                {
                    name: i18n("general.fields.price"),
                    property: "price",
                    styles: { width: "12%" },
                    formatter: (m, p) => p.toFixed(2),
                },
                {
                    name: i18n("general.fields.notes"),
                    property: "internalNote",
                    styles: { width: "19%" },
                },
            ],
            filterProvider: {
                getFilter: () => {
                    // A price per minute item has to be a main item, so it can not be an option
                    const filter = this.search!.getValue();
                    filter["excludePricePerMinute"] = true;

                    return filter;
                },
                setFilter: (d) => this.search!.setValue(d),
            },
            useJsHistory: false,
            rowClass: (m) => (this.alreadyAdded.includes(m.id) ? "readonly" : ""),
            defaultSortAsc: true,
            locationDependend: true,
            rowsPerPage: 7,
            defaultSortProperty: "internalName",
            autoload: false,
        };

        this.search = new TableFilter("col-md-8").appendTo(this.$tableFilter);
        this.search.addLinkAction(i18n("modal.addOptionToGroup.newItemBtn"), "/article/new", "la la-plus");

        this.dataTable = new DataTable<ITableViewArticle>(this.$table, this.dao.articleDataSource, tableConfig);
        this.dataTable.onClick((m, e) => {
            e.preventDefault();
            e.stopPropagation();

            const target = $(e.currentTarget);
            if (target.hasClass("readonly")) return false;

            this.checkUncheckArticle(m, $(e.currentTarget));
        });

        this.dataTable.listenTo(this.search);
    }

    public setAlreadyAddedArticles(articles: Array<string>): AddOptionToGroupWindow {
        this.alreadyAdded = articles;
        return this;
    }

    public onBeforeShow() {
        this.resetSelected();
        this.search!.reset();
        this.dataTable!.loadData(true);
    }

    public onHide() {
        this.search!.reset();
    }

    private checkUncheckArticle(article: IArticle, $row: JQuery) {
        const check = $row.find('.checkbox > input[type="checkbox"]');
        if (check.is(":checked")) {
            const idx = this.selected.findIndex((c) => c.id === article.id);
            if (idx !== -1) this.selected.splice(idx, 1);

            check.prop("checked", false);
        } else {
            this.selected.push(article);
            check.prop("checked", true);
        }

        this.checkSubmitBtn();
    }

    private resetSelected() {
        this.selected = new Array<IArticle>();
        this.$table.find('.checkbox > input[type="checkbox"]:checked').prop("checked", false);
        this.checkSubmitBtn();
    }

    private checkSubmitBtn() {
        if (this.selected.length > 0) {
            this.$cancelBtn
                .html(i18n("general.reset"))
                .unbind("click")
                .bind("click", (e) => {
                    e.preventDefault();
                    this.resetSelected();
                });

            this.$submitBtn
                .prop("disabled", false)
                .html(i18n("modal.addOptionToGroup.saveItems", { items: this.selected.length }));
        } else {
            this.$cancelBtn
                .html(i18n("general.cancel"))
                .unbind("click")
                .bind("click", (e) => {
                    e.preventDefault();
                    this.close();
                });

            this.$submitBtn.prop("disabled", true).html(i18n("general.save"));
        }
    }
}
